import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    Divider,
    Fade,
    IconButton,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ClientDashboardLayout from '../ClientDashboardLayout';
import { useNavigate } from 'react-router-dom';
import { getMetricData } from '../../../api/metric';
import { deleteCustomerScenario, listCustomerScenarios } from '../../../api/scenarios';

const currency0 = (value) =>
    Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const percent1 = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '0.0%';
    return `${num.toFixed(1)}%`;
};

const toDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const RESPONSIVE = {
    pageTitleFontSize: { xs: 24, xl: 28 },
    pageTitleLineHeight: { xs: 1.334, xl: 1.25 },
    primaryButtonFontSize: { xs: 14, xl: 15 },
    primaryButtonPaddingX: { xs: '16px', xl: '18px' },
    primaryButtonPaddingY: { xs: '6px', xl: '8px' },
    cardPadding: { xs: '20px', xl: '24px' },
    sectionPaddingX: { xs: '20px', xl: '24px' },
    metricValueFontSize: { xs: 24, xl: 28 },
    tableHeaderPadding: { xs: '16px', xl: '18px' },
    tableCellPaddingX: { xs: '12px', xl: '16px' },
    tableCellPaddingY: { xs: '16px', xl: '18px' },
    dialogPaddingX: { xs: '20px', xl: '24px' },
    dialogPaddingBottom: { xs: '24px', xl: '28px' },
    dialogPaddingTop: { xs: '60px', xl: '64px' },
    dialogTitleFontSize: { xs: 24, xl: 28 },
    dialogButtonFontSize: { xs: 15, xl: 16 },
};

const ScenarioPlanner = () => {
    const navigate = useNavigate();
    const [customerId, setCustomerId] = useState(() => sessionStorage.getItem('selectedCustomerId'));
    const endDateStr = useMemo(() => toDateStr(new Date()), []);

    const [currentPerformance, setCurrentPerformance] = useState({
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        profitMargin: 0,
    });

    useEffect(() => {
        const syncFromStorage = () => {
            setCustomerId(sessionStorage.getItem('selectedCustomerId'));
        };
        window.addEventListener('selectedCustomerId-changed', syncFromStorage);
        return () => window.removeEventListener('selectedCustomerId-changed', syncFromStorage);
    }, []);

    useEffect(() => {
        if (!customerId) return;
        const controller = new AbortController();

        const fetchCurrentPerformance = async () => {
            try {
                const res = await getMetricData(customerId, endDateStr, controller.signal, { adjusted: false, ytd: false });
                const data = res?.data || {};
                setCurrentPerformance({
                    totalRevenue: Number(data.income) || 0,
                    totalExpenses: Number(data.expense) || 0,
                    netProfit: Number(data.profit) || 0,
                    profitMargin: Number(data.profit_percentage) || 0,
                });
            } catch (err) {
                if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
                    console.error('Failed to load current performance metrics', err);
                }
            }
        };

        fetchCurrentPerformance();
        return () => controller.abort();
    }, [customerId, endDateStr]);

    const [scenarios, setScenarios] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [scenarioToDelete, setScenarioToDelete] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteBannerOpen, setDeleteBannerOpen] = useState(false);
    const [deleteBannerScenarioName, setDeleteBannerScenarioName] = useState('');
    const [deleteBannerSeverity, setDeleteBannerSeverity] = useState('success');
    const [deleteBannerMessage, setDeleteBannerMessage] = useState('');

    const deleteBannerTokens =
        deleteBannerSeverity === 'success'
            ? {
                  bg: '#EDF7ED',
                  fg: '#1E4620',
                  icon: <CheckCircleOutlinedIcon sx={{ width: 24, height: 24, color: '#1E4620' }} />,
              }
            : {
                  bg: '#FDE7E7',
                  fg: '#D32F2F',
                  icon: <ErrorOutlineIcon sx={{ width: 24, height: 24, color: '#D32F2F' }} />,
              };

    useEffect(() => {
        if (!customerId) return;
        const controller = new AbortController();

        listCustomerScenarios(customerId, {}, controller.signal)
            .then((res) => {
                const rows = res?.data;
                setScenarios(Array.isArray(rows) ? rows : []);
            })
            .catch(() => {
                setScenarios([]);
            });

        return () => controller.abort();
    }, [customerId]);

    const openDeleteDialog = (scenario) => {
        setScenarioToDelete(scenario || null);
        setDeleteError(null);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        if (deleting) return;
        setDeleteDialogOpen(false);
        setScenarioToDelete(null);
        setDeleteError(null);
    };

    const confirmDelete = async () => {
        if (!customerId || !scenarioToDelete?.id) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            const deletedName = String(scenarioToDelete?.name || '');
            await deleteCustomerScenario(customerId, scenarioToDelete.id);
            setScenarios((prev) => prev.filter((s) => s.id !== scenarioToDelete.id));
            setDeleteDialogOpen(false);
            setScenarioToDelete(null);
            setDeleteBannerScenarioName(deletedName);
            setDeleteBannerSeverity('success');
            setDeleteBannerMessage('has been successfully deleted.');
            setDeleteBannerOpen(true);
        } catch (err) {
            const data = err?.response?.data;
            const failedName = String(scenarioToDelete?.name || '');
            if (data?.error && data?.details) {
                setDeleteError(`${data.error} ${JSON.stringify(data.details)}`);
            } else if (data?.error) {
                setDeleteError(String(data.error));
            } else {
                setDeleteError('Failed to delete scenario.');
            }

            setDeleteBannerScenarioName(failedName);
            setDeleteBannerSeverity('error');
            if (data?.error) {
                setDeleteBannerMessage(String(data.error));
            } else {
                setDeleteBannerMessage('Failed to delete scenario.');
            }
            setDeleteBannerOpen(true);
        } finally {
            setDeleting(false);
        }
    };

    const formatDateTime = (value) => {
        if (!value) return '';
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return String(value);
        return dt.toLocaleDateString('en-US');
    };

    return (
        <ClientDashboardLayout title={null} contentMaxWidth="100%" contentAlign="left">
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 2.5, sm: 3 },
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 2.5, flexWrap: 'wrap' }}>
                    <Typography
                        sx={{
                            fontFamily: '"Rethink Sans"',
                            fontSize: RESPONSIVE.pageTitleFontSize,
                            fontWeight: 600,
                            lineHeight: RESPONSIVE.pageTitleLineHeight,
                            color: 'rgba(0,0,0,0.87)',
                        }}
                    >
                        Financial Scenario Planner
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon sx={{ width: 24, height: 24 }} />}
                        sx={{
                            textTransform: 'capitalize',
                            borderRadius: '4px',
                            px: RESPONSIVE.primaryButtonPaddingX,
                            py: RESPONSIVE.primaryButtonPaddingY,
                            fontFamily: '"Rethink Sans"',
                            fontWeight: 500,
                            fontSize: RESPONSIVE.primaryButtonFontSize,
                            lineHeight: '24px',
                            letterSpacing: '0.4px',
                            boxShadow: '0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.20)',
                            bgcolor: '#536DEC',
                            '&:hover': { bgcolor: '#4C63D7' },
                            '& .MuiButton-startIcon': {
                                marginLeft: 0,
                                marginRight: '8px',
                            },
                            '& .MuiButton-startIcon > *:nth-of-type(1)': {
                                fontSize: 24,
                            },
                        }}
                        onClick={() => {
                            navigate('/scenario-planner/create');
                        }}
                    >
                        Create New Scenario
                    </Button>
                </Stack>

                <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />

                <Card
                    sx={{
                        borderRadius: '12px',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                    }}
                >
                    <CardContent sx={{ p: RESPONSIVE.cardPadding, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontFamily: '"Rethink Sans"', fontSize: 18, fontWeight: 600, lineHeight: 1.6, letterSpacing: '0.15px', color: 'rgba(0,0,0,0.87)' }}>
                                Your Current Performance
                            </Typography>
                            <Chip
                                icon={<LockOutlinedIcon sx={{ width: 16, height: 16 }} />}
                                label="Baseline"
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(0,0,0,0.08)',
                                    color: 'rgba(0,0,0,0.56)',
                                    fontFamily: '"Rethink Sans"',
                                    fontWeight: 500,
                                    borderRadius: '100px',
                                    px: '8px',
                                    py: '4px',
                                }}
                            />
                        </Stack>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                                gap: 2.5,
                            }}
                        >
                            {[
                                { label: 'Total Revenue', value: currency0(currentPerformance.totalRevenue), valueColor: 'rgba(23,25,35,0.9)' },
                                { label: 'Total Expenses', value: currency0(currentPerformance.totalExpenses), valueColor: 'rgba(23,25,35,0.9)' },
                                { label: 'Net Profit', value: currency0(currentPerformance.netProfit), valueColor: currentPerformance.netProfit < 0 ? '#D32F2F' : '#2E7D32' },
                                { label: 'Profit Margin', value: percent1(currentPerformance.profitMargin), valueColor: currentPerformance.profitMargin < 0 ? '#D32F2F' : '#2E7D32' },
                            ].map((stat) => (
                                <Card
                                    key={stat.label}
                                    variant="outlined"
                                    sx={{
                                        bgcolor: '#FDFDFD',
                                        borderRadius: '10px',
                                        border: '1px solid rgba(0,0,0,0.04)',
                                        boxShadow: '0px 0px 12px 0px rgba(0,0,0,0.04)',
                                        minHeight: '84px',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            px: '16px',
                                            pt: '12px',
                                            pb: '12px',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            '&:last-child': {
                                                paddingBottom: '12px',
                                            },
                                        }}
                                    >
                                        <Typography sx={{ fontFamily: '"Rethink Sans"', fontSize: 14, color: 'rgba(23,25,35,0.7)', lineHeight: '21px' }}>
                                            {stat.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: '"Rethink Sans"',
                                                fontSize: RESPONSIVE.metricValueFontSize,
                                                fontWeight: 600,
                                                lineHeight: '38.4px',
                                                color: stat.valueColor,
                                            }}
                                        >
                                            {stat.value}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        borderRadius: '10px',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                    }}
                >
                    <CardContent
                        sx={{
                            py: RESPONSIVE.cardPadding,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '24px',
                            px: 0,
                        }}
                    >
                        <Stack spacing={2} sx={{ px: RESPONSIVE.sectionPaddingX }}>
                            <Typography
                                sx={{
                                    fontFamily: '"Rethink Sans"',
                                    fontSize: 18,
                                    fontWeight: 600,
                                    lineHeight: 1.6,
                                    letterSpacing: '0.15px',
                                    color: 'rgba(0,0,0,0.87)',
                                }}
                            >
                                Saved Scenarios ({scenarios.length})
                            </Typography>
                        </Stack>
                        <Divider />

                        <Stack spacing={2} sx={{ px: RESPONSIVE.sectionPaddingX }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
                                    gap: 2,
                                    alignItems: 'start',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <Typography
                                        sx={{
                                            fontFamily: '"Rethink Sans"',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            lineHeight: 1.57,
                                            letterSpacing: '0.1px',
                                            color: '#374151',
                                        }}
                                    >
                                        Search
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Search by scenario name..."
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: 40,
                                                borderRadius: '8px',
                                            },
                                            '& .MuiInputBase-input': {
                                                px: 0,
                                                py: 0,
                                                fontFamily: '"Rethink Sans"',
                                                fontSize: 14,
                                                letterSpacing: '0.17px',
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                color: 'rgba(0,0,0,0.38)',
                                                opacity: 1,
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                px: '12px',
                                            },
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <Typography
                                        sx={{
                                            fontFamily: '"Rethink Sans"',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            lineHeight: 1.57,
                                            letterSpacing: '0.1px',
                                            color: '#374151',
                                        }}
                                    >
                                        Impact Type
                                    </Typography>
                                    <Select
                                        size="small"
                                        fullWidth
                                        displayEmpty
                                        value=""
                                        sx={{
                                            height: 40,
                                            borderRadius: '8px',
                                            '& .MuiSelect-select': {
                                                display: 'flex',
                                                alignItems: 'center',
                                                px: '12px',
                                                py: 0,
                                                fontFamily: '"Rethink Sans"',
                                                fontSize: 14,
                                                letterSpacing: '0.17px',
                                                color: 'rgba(0,0,0,0.38)',
                                            },
                                        }}
                                    >
                                        <MenuItem value="">All Impact Types</MenuItem>
                                        <MenuItem value="revenue">Revenue</MenuItem>
                                        <MenuItem value="expense">Expense</MenuItem>
                                        <MenuItem value="profit">Profit</MenuItem>
                                    </Select>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <Typography
                                        sx={{
                                            fontFamily: '"Rethink Sans"',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            lineHeight: 1.57,
                                            letterSpacing: '0.1px',
                                            color: '#374151',
                                        }}
                                    >
                                        Sort By
                                    </Typography>
                                    <Select
                                        size="small"
                                        fullWidth
                                        value="newest"
                                        sx={{
                                            height: 40,
                                            borderRadius: '8px',
                                            '& .MuiSelect-select': {
                                                display: 'flex',
                                                alignItems: 'center',
                                                px: '12px',
                                                py: 0,
                                                fontFamily: '"Rethink Sans"',
                                                fontSize: 14,
                                                letterSpacing: '0.17px',
                                                color: 'rgba(0,0,0,0.38)',
                                            },
                                        }}
                                    >
                                        <MenuItem value="newest">Newest First</MenuItem>
                                        <MenuItem value="oldest">Oldest First</MenuItem>
                                        <MenuItem value="impact">Highest Impact</MenuItem>
                                    </Select>
                                </Box>
                            </Box>
                        </Stack>

                        <Box sx={{ px: RESPONSIVE.sectionPaddingX, maxWidth: '100%', overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 900 }}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#EEF0FD' }}>
                                        <TableCell
                                            sx={{
                                                p: RESPONSIVE.tableHeaderPadding,
                                                fontFamily: '"Rethink Sans"',
                                                fontWeight: 500,
                                                fontSize: 15,
                                                letterSpacing: '0.17px',
                                                textTransform: 'capitalize',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Scenario Name
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                p: RESPONSIVE.tableHeaderPadding,
                                                width: 140,
                                                fontFamily: '"Rethink Sans"',
                                                fontWeight: 500,
                                                fontSize: 15,
                                                letterSpacing: '0.17px',
                                                textTransform: 'capitalize',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Key Metrics
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                p: RESPONSIVE.tableHeaderPadding,
                                                width: 140,
                                                fontFamily: '"Rethink Sans"',
                                                fontWeight: 500,
                                                fontSize: 15,
                                                letterSpacing: '0.17px',
                                                textTransform: 'capitalize',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Profit Impact
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                p: RESPONSIVE.tableHeaderPadding,
                                                width: 140,
                                                fontFamily: '"Rethink Sans"',
                                                fontWeight: 500,
                                                fontSize: 15,
                                                letterSpacing: '0.17px',
                                                textTransform: 'capitalize',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Date Created
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                p: RESPONSIVE.tableHeaderPadding,
                                                width: 140,
                                                fontFamily: '"Rethink Sans"',
                                                fontWeight: 500,
                                                fontSize: 15,
                                                letterSpacing: '0.17px',
                                                textTransform: 'capitalize',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Last Updated
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                p: RESPONSIVE.tableHeaderPadding,
                                                width: 140,
                                                fontFamily: '"Rethink Sans"',
                                                fontWeight: 500,
                                                fontSize: 15,
                                                letterSpacing: '0.17px',
                                                textTransform: 'capitalize',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {scenarios.map((s) => (
                                        <TableRow key={s.id} hover>
                                            <TableCell
                                                sx={{
                                                    px: RESPONSIVE.tableCellPaddingX,
                                                    py: RESPONSIVE.tableCellPaddingY,
                                                    verticalAlign: 'middle',
                                                    minWidth: 260,
                                                }}
                                            >
                                                <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 400, fontSize: 14, letterSpacing: '0.17px', color: 'rgba(0,0,0,0.87)' }}>
                                                    {s.name}
                                                </Typography>
                                                <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 400, fontSize: 14, letterSpacing: '0.17px', color: 'rgba(0,0,0,0.87)' }}>
                                                    {s.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    px: RESPONSIVE.tableCellPaddingX,
                                                    py: RESPONSIVE.tableCellPaddingY,
                                                    width: 150,
                                                    verticalAlign: 'top',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {(() => {
                                                    const metrics = s.scenario_metrics || {};
                                                    const profit = Number(metrics.netProfit) || 0;
                                                    const margin = Number(metrics.profitMarginPct) || 0;
                                                    return (
                                                        <>
                                                <Typography sx={{ fontFamily: '"Rethink Sans"', fontSize: 14, letterSpacing: '0.17px', color: 'rgba(0,0,0,0.87)' }}>
                                                    <Box component="span" sx={{ color: 'rgba(0,0,0,0.87)' }}>Profit: </Box>
                                                    <Box component="span" sx={{ color: 'rgba(0,0,0,0.60)', fontWeight: 600, letterSpacing: '0.1px' }}>
                                                                {currency0(profit)}
                                                    </Box>
                                                </Typography>
                                                <Typography sx={{ fontFamily: '"Rethink Sans"', fontSize: 14, letterSpacing: '0.17px', color: 'rgba(0,0,0,0.87)' }}>
                                                    <Box component="span" sx={{ color: 'rgba(0,0,0,0.87)' }}>Margin: </Box>
                                                    <Box component="span" sx={{ color: 'rgba(0,0,0,0.60)', fontWeight: 600, letterSpacing: '0.1px' }}>
                                                                {percent1(margin)}
                                                    </Box>
                                                </Typography>
                                                        </>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    px: RESPONSIVE.tableCellPaddingX,
                                                    py: RESPONSIVE.tableCellPaddingY,
                                                    width: 130,
                                                    verticalAlign: 'middle',
                                                }}
                                            >
                                                {(() => {
                                                    const baselineProfit = Number(s.baseline_metrics?.netProfit);
                                                    const scenarioProfit = Number(s.scenario_metrics?.netProfit);
                                                    const hasBaseline = Number.isFinite(baselineProfit) && baselineProfit !== 0;
                                                    const pct = hasBaseline ? ((scenarioProfit - baselineProfit) / Math.abs(baselineProfit)) * 100 : 0;
                                                    const displayPct = Number.isFinite(pct) ? pct : 0;
                                                    const positive = displayPct >= 0;
                                                    return (
                                                <Chip
                                                    size="small"
                                                    icon={
                                                        positive ? (
                                                            <ArrowUpwardIcon sx={{ width: 16, height: 16 }} />
                                                        ) : (
                                                            <ArrowDownwardIcon sx={{ width: 16, height: 16 }} />
                                                        )
                                                    }
                                                            label={`${positive ? '+' : ''}${displayPct.toFixed(1)}%`}
                                                    sx={{
                                                                bgcolor: positive ? '#E6F9F2' : '#FDE7E7',
                                                                color: positive ? '#43A047' : '#D32F2F',
                                                        fontFamily: '"Rethink Sans"',
                                                        fontWeight: 400,
                                                        fontSize: 14,
                                                        letterSpacing: '0.5px',
                                                        borderRadius: '6px',
                                                        padding: '2px 5px',
                                                                '& .MuiChip-icon': { ml: 0, mr: '5px', color: positive ? '#43A047' : '#D32F2F' },
                                                        '& .MuiChip-label': { px: 0, lineHeight: '21px' },
                                                    }}
                                                />
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    p: RESPONSIVE.tableHeaderPadding,
                                                    width: 140,
                                                    verticalAlign: 'middle',
                                                    fontFamily: '"Rethink Sans"',
                                                    fontSize: 14,
                                                    letterSpacing: '0.17px',
                                                    color: 'rgba(0,0,0,0.87)',
                                                }}
                                            >
                                                {formatDateTime(s.created_on)}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    p: RESPONSIVE.tableHeaderPadding,
                                                    width: 140,
                                                    verticalAlign: 'middle',
                                                    fontFamily: '"Rethink Sans"',
                                                    fontSize: 14,
                                                    letterSpacing: '0.17px',
                                                    color: 'rgba(0,0,0,0.87)',
                                                }}
                                            >
                                                {formatDateTime(s.modified_on)}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    px: RESPONSIVE.tableCellPaddingX,
                                                    py: RESPONSIVE.tableCellPaddingY,
                                                    verticalAlign: 'top',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Stack direction="row" spacing={1.75} justifyContent="center">
                                                    <IconButton
                                                        size="small"
                                                        sx={{ border: '1px solid rgba(83,109,236,0.7)', color: '#536DEC', borderRadius: '8px', p: '5px' }}
                                                        onClick={() => {
                                                            navigate(`/scenario-planner/${s.id}/edit`);
                                                        }}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ border: '1px solid rgba(0,0,0,0.35)', color: 'rgba(0,0,0,0.65)', borderRadius: '8px', p: '5px' }}
                                                        onClick={() => {
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src="/analytics-icon.svg"
                                                            alt="Analytics"
                                                            sx={{ width: 24, height: 24, display: 'block' }}
                                                        />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ border: '1px solid rgba(211,47,47,0.75)', color: '#D32F2F', borderRadius: '8px', p: '5px' }}
                                                        onClick={() => {
                                                            openDeleteDialog(s);
                                                        }}
                                                    >
                                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <Dialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        position: 'relative',
                        borderRadius: '16px',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
                        overflow: 'visible',
                        pt: RESPONSIVE.dialogPaddingTop,
                        pb: RESPONSIVE.dialogPaddingBottom,
                        px: RESPONSIVE.dialogPaddingX,
                    },
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-40px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 80,
                        height: 80,
                        bgcolor: '#E53935',
                        borderRadius: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <WarningAmberRoundedIcon sx={{ width: 40, height: 40, color: '#FFFFFF' }} />
                </Box>

                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'center' }}>
                        <Typography
                            sx={{
                                fontFamily: '"Rethink Sans"',
                                fontWeight: 600,
                                fontSize: RESPONSIVE.dialogTitleFontSize,
                                lineHeight: 1.334,
                                color: 'rgba(0,0,0,0.87)',
                            }}
                        >
                            Are you sure?
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: '"Rethink Sans"',
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: 1.5,
                                letterSpacing: '0.15px',
                                color: 'rgba(0,0,0,0.6)',
                            }}
                        >
                            <Box component="span" sx={{ lineHeight: 1.5 }}>
                                Are you sure you want to delete{' '}
                                <Box component="span" sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.87)' }}>
                                    {scenarioToDelete?.name || 'this scenario'}
                                </Box>
                                <Box component="span" sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.87)' }}>
                                    ?
                                </Box>
                            </Box>
                            <Box component="span" sx={{ display: 'block', lineHeight: 1.5 }}>
                                This action cannot be undone.
                            </Box>
                        </Typography>
                    </Box>

                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={deleting}
                            onClick={confirmDelete}
                            sx={{
                                textTransform: 'capitalize',
                                borderRadius: '8px',
                                px: '22px',
                                py: '8px',
                                fontFamily: '"Rethink Sans"',
                                fontWeight: 500,
                                fontSize: RESPONSIVE.dialogButtonFontSize,
                                lineHeight: '26px',
                                letterSpacing: '0.46px',
                                bgcolor: '#D32F2F',
                                boxShadow:
                                    '0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.20)',
                                '&:hover': { bgcolor: '#C62828' },
                            }}
                        >
                            Delete Scenario
                        </Button>

                        <Button
                            variant="outlined"
                            fullWidth
                            disabled={deleting}
                            onClick={closeDeleteDialog}
                            sx={{
                                textTransform: 'capitalize',
                                borderRadius: '8px',
                                px: '22px',
                                py: '8px',
                                fontFamily: '"Rethink Sans"',
                                fontWeight: 500,
                                fontSize: RESPONSIVE.dialogButtonFontSize,
                                lineHeight: '26px',
                                letterSpacing: '0.46px',
                                borderColor: 'rgba(0,0,0,0.87)',
                                color: 'rgba(0,0,0,0.87)',
                                '&:hover': { borderColor: 'rgba(0,0,0,0.87)', bgcolor: 'rgba(0,0,0,0.04)' },
                            }}
                        >
                            Cancel
                        </Button>

                        {deleteError && (
                            <Typography
                                sx={{
                                    fontFamily: '"Rethink Sans"',
                                    fontWeight: 400,
                                    fontSize: 14,
                                    lineHeight: 1.43,
                                    letterSpacing: '0.17px',
                                    color: '#D32F2F',
                                    textAlign: 'center',
                                }}
                            >
                                {deleteError}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Dialog>

            <Snackbar
                open={deleteBannerOpen}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                autoHideDuration={10000}
                TransitionComponent={Fade}
                onClose={(_, reason) => {
                    if (reason === 'clickaway') return;
                    setDeleteBannerOpen(false);
                }}
                sx={{ zIndex: 2100 }}
            >
                <Alert
                    icon={deleteBannerTokens.icon}
                    severity={deleteBannerSeverity}
                    variant="standard"
                    sx={{
                        bgcolor: deleteBannerTokens.bg,
                        borderRadius: '4px',
                        px: '16px',
                        py: '6px',
                        alignItems: 'flex-start',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
                        '& .MuiAlert-icon': {
                            mr: '12px',
                            pt: '7px',
                            pb: '7px',
                        },
                        '& .MuiAlert-message': {
                            py: '8px',
                            px: 0,
                            width: '100%',
                        },
                        '& .MuiAlert-action': {
                            pl: '16px',
                            pt: '4px',
                            mr: 0,
                        },
                    }}
                    action={
                        <IconButton
                            size="small"
                            aria-label="Close"
                            onClick={() => setDeleteBannerOpen(false)}
                            sx={{ p: '5px', borderRadius: '100px', color: deleteBannerTokens.fg }}
                        >
                            <CloseIcon sx={{ width: 24, height: 24 }} />
                        </IconButton>
                    }
                >
                    <Typography
                        sx={{
                            fontFamily: '"Rethink Sans"',
                            fontWeight: 500,
                            fontSize: 16,
                            lineHeight: 1.5,
                            letterSpacing: '0.15px',
                            color: deleteBannerTokens.fg,
                        }}
                    >
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {`‘${deleteBannerScenarioName || 'Scenario'}’`}
                        </Box>{' '}
                        {deleteBannerSeverity === 'success' ? deleteBannerMessage : `could not be deleted. ${deleteBannerMessage}`}
                    </Typography>
                </Alert>
            </Snackbar>
        </ClientDashboardLayout>
    );
};

export default ScenarioPlanner;
